import React, { useState } from 'react';
import { leadImportService, LeadImportData, Child } from '../../services/leadImportService';

interface ManualLeadFormProps {
  onSuccess: (lead: any) => void;
  onCancel: () => void;
}

const ManualLeadForm: React.FC<ManualLeadFormProps> = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<LeadImportData>({
    email: '',
    parent_name: '',
    phone: '',
    timeline: 'Just exploring',
    timeline_date: '',
    biggest_concerns: '',
    children: [
      {
        name: '',
        age: undefined,
        grade: '',
        special_needs: [],
        current_support: []
      }
    ]
  });

  // Available options
  const timelineOptions = [
    'Immediately',
    'Next semester',
    'Next school year',
    'Just exploring'
  ];

  const specialNeedsOptions = [
    'Autism/ASD',
    'ADHD',
    'Learning Disabilities',
    'Dyslexia',
    'Speech/Language Delays',
    'Sensory Processing',
    'Anxiety/Depression',
    'Physical Disabilities',
    'Intellectual Disabilities',
    'Other'
  ];

  const currentSupportOptions = [
    'IEP',
    '504 Plan',
    'Speech therapy',
    'Occupational Therapy (OT)',
    'Physical Therapy (PT)',
    'Behavioral therapy',
    'Tutoring',
    'Shadow aide',
    'Special education services',
    'None currently'
  ];

  const gradeOptions = [
    'Pre-K',
    'Kindergarten',
    '1st Grade',
    '2nd Grade',
    '3rd Grade',
    '4th Grade',
    '5th Grade',
    '6th Grade',
    '7th Grade',
    '8th Grade',
    '9th Grade',
    '10th Grade',
    '11th Grade',
    '12th Grade'
  ];

  const handleInputChange = (field: keyof LeadImportData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChildChange = (childIndex: number, field: keyof Child, value: any) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, index) =>
        index === childIndex ? { ...child, [field]: value } : child
      )
    }));
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [
        ...prev.children,
        {
          name: '',
          age: undefined,
          grade: '',
          special_needs: [],
          current_support: []
        }
      ]
    }));
  };

  const removeChild = (childIndex: number) => {
    if (formData.children.length > 1) {
      setFormData(prev => ({
        ...prev,
        children: prev.children.filter((_, index) => index !== childIndex)
      }));
    }
  };

  const toggleSpecialNeed = (childIndex: number, need: string) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, index) => {
        if (index === childIndex) {
          const currentNeeds = child.special_needs || [];
          const updatedNeeds = currentNeeds.includes(need)
            ? currentNeeds.filter(n => n !== need)
            : [...currentNeeds, need];
          return { ...child, special_needs: updatedNeeds };
        }
        return child;
      })
    }));
  };

  const toggleCurrentSupport = (childIndex: number, support: string) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.map((child, index) => {
        if (index === childIndex) {
          const currentSupport = child.current_support || [];
          const updatedSupport = currentSupport.includes(support)
            ? currentSupport.filter(s => s !== support)
            : [...currentSupport, support];
          return { ...child, current_support: updatedSupport };
        }
        return child;
      })
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.email) return 'Email is required';
    if (!formData.parent_name) return 'Parent name is required';
    if (formData.children.length === 0) return 'At least one child is required';

    for (let i = 0; i < formData.children.length; i++) {
      const child = formData.children[i];
      if (!child.name) return `Child ${i + 1} name is required`;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📝 Submitting manual lead:', formData);

      const result = await leadImportService.createSingleLead(formData);

      console.log('✅ Lead created successfully:', result);
      setSuccessMessage(`Lead created successfully! Assigned to ${result.segments.length} segments.`);

      // Call success callback after a brief delay to show success message
      setTimeout(() => {
        onSuccess(result.lead);
      }, 1500);

    } catch (err) {
      console.error('❌ Failed to create lead:', err);
      setError(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add New Lead</h2>
              <p className="text-sm text-gray-500">Manually create a new lead with multiple children support</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors duration-200"
              disabled={loading}
            >
              ×
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
              <span className="text-green-600">✅</span>
              <span className="text-green-700 font-medium">{successMessage}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
              <span className="text-red-600">❌</span>
              <span className="text-red-700 font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Parent Information */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span>
                Parent Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Name *
                  </label>
                  <input
                    type="text"
                    value={formData.parent_name}
                    onChange={(e) => handleInputChange('parent_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter parent's full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="parent@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeline
                  </label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {timelineOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Specific Start Date (optional)
                </label>
                <input
                  type="date"
                  value={formData.timeline_date}
                  onChange={(e) => handleInputChange('timeline_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Concerns or Notes
                </label>
                <textarea
                  value={formData.biggest_concerns}
                  onChange={(e) => handleInputChange('biggest_concerns', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the main challenges or concerns for the children..."
                />
              </div>
            </div>

            {/* Children Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span>👶</span>
                  Children Information ({formData.children.length})
                </h3>
                <button
                  type="button"
                  onClick={addChild}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  + Add Child
                </button>
              </div>

              <div className="space-y-4">
                {formData.children.map((child, childIndex) => (
                  <div key={childIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-base font-medium text-gray-900">
                        Child {childIndex + 1}
                      </h4>
                      {formData.children.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeChild(childIndex)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Child Name *
                        </label>
                        <input
                          type="text"
                          value={child.name}
                          onChange={(e) => handleChildChange(childIndex, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter child's name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Age
                        </label>
                        <input
                          type="number"
                          value={child.age || ''}
                          onChange={(e) => handleChildChange(childIndex, 'age', e.target.value ? parseInt(e.target.value) : undefined)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Age"
                          min="0"
                          max="25"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Grade
                        </label>
                        <select
                          value={child.grade}
                          onChange={(e) => handleChildChange(childIndex, 'grade', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select grade...</option>
                          {gradeOptions.map(grade => (
                            <option key={grade} value={grade}>{grade}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Special Needs
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {specialNeedsOptions.map(need => (
                          <label key={need} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={child.special_needs?.includes(need) || false}
                              onChange={() => toggleSpecialNeed(childIndex, need)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{need}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Support Services
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {currentSupportOptions.map(support => (
                          <label key={support} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={child.current_support?.includes(support) || false}
                              onChange={() => toggleCurrentSupport(childIndex, support)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span>{support}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={loading}
              >
                {loading ? 'Creating Lead...' : 'Create Lead'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManualLeadForm;